import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchOrderDetails } from '../../../../services/orders/orderDetailsService';
import type { Item, LayoutInfo, Pedido } from '../../../../types/orders';
import {
  copyImageBlobToClipboard,
  copyImageToClipboard,
} from '../../../../utils/copyImageToClipboard';
import { notify } from '../../../../lib/globalNotify';

interface OrcLayoutsSectionProps {
  orcId: number;
}

interface ItemLayouts {
  item: Item;
  layouts: LayoutInfo[];
}

interface LightboxTarget {
  title: string;
  /** Bytes já baixados (miniatura). Usados para exibir e copiar sem novo download. */
  blob: Blob | null;
  /** Fallback: URL original, caso o blob não esteja disponível. */
  url: string;
}

type LayoutImageStatus = 'loading' | 'ready' | 'error';

interface LayoutImageState {
  objectUrl: string | null;
  blob: Blob | null;
  status: LayoutImageStatus;
}

/**
 * Baixa a imagem do layout uma única vez e expõe um object URL para renderizar.
 * Renderizar a partir do blob (e não de `<img src={urlRemota}>`) evita que o
 * `<img>` seja recusado quando o CDN serve o arquivo com Content-Type genérico
 * + nosniff — os bytes continuam sendo uma imagem válida.
 */
function useLayoutImage(url: string): LayoutImageState {
  const [state, setState] = useState<LayoutImageState>({
    objectUrl: null,
    blob: null,
    status: url ? 'loading' : 'error',
  });

  useEffect(() => {
    if (!url) {
      setState({ objectUrl: null, blob: null, status: 'error' });
      return;
    }

    let active = true;
    let createdUrl: string | null = null;
    setState({ objectUrl: null, blob: null, status: 'loading' });

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.blob();
      })
      .then((blob) => {
        if (!active) {
          return;
        }
        createdUrl = URL.createObjectURL(blob);
        setState({ objectUrl: createdUrl, blob, status: 'ready' });
      })
      .catch(() => {
        if (active) {
          setState({ objectUrl: null, blob: null, status: 'error' });
        }
      });

    return () => {
      active = false;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [url]);

  return state;
}

async function runCopy(
  source: { blob: Blob | null; url: string },
  setCopying: (value: boolean) => void,
): Promise<void> {
  setCopying(true);
  try {
    if (source.blob) {
      await copyImageBlobToClipboard(source.blob);
    } else if (source.url) {
      await copyImageToClipboard(source.url);
    } else {
      throw new Error('Imagem indisponível para cópia.');
    }
    notify({
      type: 'success',
      title: 'Imagem copiada',
      message: 'Cole no CorelDRAW com Ctrl+V.',
    });
  } catch (error) {
    notify({
      type: 'error',
      title: 'Não foi possível copiar',
      message: error instanceof Error ? error.message : 'Erro ao copiar a imagem.',
    });
  } finally {
    setCopying(false);
  }
}

const CopyButton: React.FC<{ blob: Blob | null; url: string; className?: string }> = ({
  blob,
  url,
  className,
}) => {
  const [copying, setCopying] = useState(false);

  return (
    <button
      type="button"
      disabled={copying}
      onClick={() => void runCopy({ blob, url }, setCopying)}
      className={
        'shrink-0 rounded-md border border-emerald-400/50 bg-emerald-500/20 px-3 py-1.5 text-[11px] font-semibold text-emerald-100 transition-colors hover:bg-emerald-500/35 hover:text-white disabled:cursor-not-allowed disabled:opacity-60 ' +
        (className ?? '')
      }
    >
      {copying ? 'Copiando…' : 'Copiar'}
    </button>
  );
};

const LayoutFileRow: React.FC<{
  layout: LayoutInfo;
  label: string;
  onOpen: (target: LightboxTarget) => void;
}> = ({ layout, label, onOpen }) => {
  const title = layout.ola_arquivo?.trim() || label;
  const layoutUrl = layout.ola_url?.trim() ?? '';
  const { objectUrl, blob, status } = useLayoutImage(layoutUrl);

  const canPreview = status === 'ready' && objectUrl != null;
  const openLightbox = () => onOpen({ title, blob, url: layoutUrl });

  return (
    <div className="flex items-center gap-3 rounded-lg border border-navy-700 bg-navy-900/30 px-3 py-2">
      <button
        type="button"
        disabled={!canPreview}
        onClick={() => canPreview && openLightbox()}
        className="relative h-11 w-11 shrink-0 overflow-hidden rounded-md border border-navy-600 bg-navy-950/60 disabled:cursor-default"
        title={canPreview ? 'Ampliar' : undefined}
        aria-label={canPreview ? `Ampliar ${title}` : title}
      >
        {canPreview ? (
          <img src={objectUrl ?? ''} alt={title} className="h-full w-full object-cover" />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-base text-gray-500">
            {status === 'loading' ? '⏳' : '🖼️'}
          </span>
        )}
      </button>

      <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-white" title={title}>
        {title}
      </span>

      {layoutUrl && (
        <div className="flex shrink-0 items-center gap-1.5">
          {canPreview && (
            <button
              type="button"
              onClick={openLightbox}
              className="rounded-md border border-cyan-400/50 bg-cyan-500/20 px-3 py-1.5 text-[11px] font-semibold text-cyan-100 transition-colors hover:bg-cyan-500/35 hover:text-white"
            >
              Ampliar
            </button>
          )}
          <CopyButton blob={blob} url={layoutUrl} />
        </div>
      )}
    </div>
  );
};

const LayoutLightbox: React.FC<{ target: LightboxTarget; onClose: () => void }> = ({ target, onClose }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!target.blob) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(target.blob);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [target.blob]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const imageSrc = objectUrl ?? target.url;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-navy-600 bg-navy-800 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-navy-600 px-4 py-3">
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-white" title={target.title}>
            {target.title}
          </span>
          <div className="flex items-center gap-1.5">
            <CopyButton blob={target.blob} url={target.url} />
            <button
              type="button"
              onClick={onClose}
              className="p-1 text-lg leading-none text-gray-400 transition-colors hover:text-gray-100"
              aria-label="Fechar"
            >
              ×
            </button>
          </div>
        </div>
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-navy-950/40 p-4">
          {imageSrc ? (
            <img src={imageSrc} alt={target.title} className="max-h-full max-w-full object-contain" />
          ) : (
            <span className="text-sm text-gray-400">Não foi possível carregar a imagem.</span>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export const OrcLayoutsSection: React.FC<OrcLayoutsSectionProps> = ({ orcId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [orderDetails, setOrderDetails] = useState<Pedido | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [lightbox, setLightbox] = useState<LightboxTarget | null>(null);

  const orderLayouts = orderDetails?.orcamento_layout ?? [];
  const itemLayouts = useMemo<ItemLayouts[]>(() => {
    const result: ItemLayouts[] = [];

    for (const item of orderDetails?.itens ?? []) {
      if (Array.isArray(item.orcamento_layout) && item.orcamento_layout.length > 0) {
        result.push({ item, layouts: item.orcamento_layout });
      }
    }

    return result;
  }, [orderDetails?.itens]);

  useEffect(() => {
    let isCurrentRequest = true;

    setOrderDetails(null);
    setHasError(false);

    if (!orcId) {
      setIsLoading(false);
      setHasError(true);
      return () => {
        isCurrentRequest = false;
      };
    }

    setIsLoading(true);
    void fetchOrderDetails(orcId)
      .then((details) => {
        if (isCurrentRequest) {
          setOrderDetails(details);
        }
      })
      .catch(() => {
        if (isCurrentRequest) {
          setHasError(true);
        }
      })
      .finally(() => {
        if (isCurrentRequest) {
          setIsLoading(false);
        }
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [orcId]);

  return (
    <div className="space-y-2" data-testid="orc-layouts-section">
      <button
        type="button"
        onClick={() => setIsExpanded((expanded) => !expanded)}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-navy-600 bg-navy-900/30 px-2 py-1.5 text-[11px] font-medium text-gray-300 transition-colors hover:bg-navy-900/50"
        aria-expanded={isExpanded}
      >
        <span>{isExpanded ? 'Ocultar layouts' : 'Ver layouts'}</span>
        <span className="text-gray-500">{isExpanded ? '▲' : '▼'}</span>
      </button>

      {isExpanded && (
        <div className="space-y-3 rounded-lg border border-navy-700 bg-navy-900/20 p-3">
          {isLoading ? (
            <div className="text-[11px] text-gray-400">Carregando layouts...</div>
          ) : hasError ? (
            <div className="text-[11px] text-red-200">
              Não foi possível carregar os layouts deste pedido.
            </div>
          ) : orderLayouts.length === 0 && itemLayouts.length === 0 ? (
            <div className="text-center text-[11px] text-gray-400">
              Nenhum layout vinculado a este pedido.
            </div>
          ) : (
            <>
              {orderLayouts.length > 0 && (
                <div className="space-y-2">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    Do orçamento
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    {orderLayouts.map((layout, index) => (
                      <LayoutFileRow
                        key={`order-layout-${layout.ola_id ?? index}`}
                        layout={layout}
                        label={`Layout ${index + 1}`}
                        onOpen={setLightbox}
                      />
                    ))}
                  </div>
                </div>
              )}

              {itemLayouts.length > 0 && (
                <div className="space-y-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    Dos itens
                  </div>
                  {itemLayouts.map(({ item, layouts }) => (
                    <div key={`item-layouts-${item.oit_id}`} className="space-y-2">
                      <div className="text-[11px] font-medium text-gray-200">
                        {`I-${item.oit_id} · ${item.produto?.nome ?? 'Produto'}`}
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        {layouts.map((layout, index) => (
                          <LayoutFileRow
                            key={`item-${item.oit_id}-layout-${layout.ola_id ?? index}`}
                            layout={layout}
                            label={`Layout ${index + 1}`}
                            onOpen={setLightbox}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {lightbox && <LayoutLightbox target={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
};
